/**
 * Mobile shell of the customer portal (tracker item 45, phase 3): the
 * company name, the portal nav (Home, Invoices, Jobs, Estimates, Requests, Profile) and Log out.
 * No staff sidebar. An unlinked login ("we're matching your account") gets
 * Home, Requests (it may ask for a service) and Log out: nothing else is available to it yet.
 */
import React, { useEffect } from 'react';
import { Navigate, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { PORTAL_HOME_PATH, PORTAL_LOGIN_PATH, PORTAL_NAV, unlinkedAllowed } from '../../utils/portal';
import { PortalProvider, usePortal } from './PortalContext';
import { usePortalUnreadMessages } from './PortalMessagesPage';

const linkClass = ({ isActive }: { isActive: boolean }) =>
  `inline-flex items-center min-h-[44px] px-3 text-sm font-semibold rounded-md ${
    isActive ? 'bg-blue-50 text-blue-800' : 'text-gray-700 hover:bg-gray-50'
  }`;

function Shell() {
  const { me } = usePortal();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const linked = me.status === 'LINKED';
  const unreadMessages = usePortalUnreadMessages(linked, pathname);

  useEffect(() => {
    const previous = document.title;
    document.title = `${me.companyName} - Customer portal`;
    return () => { document.title = previous; };
  }, [me.companyName]);

  // Everything but the home page and the service request pages needs a customer link
  if (!linked && !unlinkedAllowed(pathname)) return <Navigate to={PORTAL_HOME_PATH} replace />;

  const signOut = async () => {
    try {
      await logout();
    } finally {
      navigate(PORTAL_LOGIN_PATH, { replace: true });
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <header className="sticky top-0 z-20 bg-white shadow">
        <div className="mx-auto max-w-3xl px-3 pt-2">
          <div className="flex items-baseline justify-between gap-3">
            <p className="min-w-0 truncate text-base font-bold text-gray-900" data-testid="portal-company">{me.companyName}</p>
            <p className="min-w-0 truncate text-xs text-gray-500" data-testid="portal-user">{me.user.email}</p>
          </div>
          <nav aria-label="Customer portal" className="mt-1 flex flex-wrap items-center gap-1 pb-2">
            {(linked ? PORTAL_NAV : PORTAL_NAV.filter((item) => item.unlinked)).map((item) => (
              <NavLink key={item.to} to={item.to} end={item.end} className={linkClass}>
                {item.label}
                {item.to === '/portal/messages' && unreadMessages > 0 && (
                  <span
                    data-testid="portal-unread-badge"
                    aria-label={`${unreadMessages} new`}
                    className="ml-1.5 inline-flex min-w-[1.25rem] justify-center rounded-full bg-blue-600 px-1.5 text-xs font-semibold text-white"
                  >
                    {unreadMessages}
                  </span>
                )}
              </NavLink>
            ))}
            <button type="button" onClick={() => void signOut()} className="ml-auto inline-flex min-h-[44px] items-center rounded-md px-3 text-sm font-semibold text-gray-700 hover:bg-gray-50">
              Log out
            </button>
          </nav>
        </div>
      </header>
      <main className="mx-auto max-w-3xl px-4 py-4 pb-16">
        <Outlet />
      </main>
    </div>
  );
}

export function PortalLayout() {
  return (
    <PortalProvider>
      <Shell />
    </PortalProvider>
  );
}
